import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseurl } from "../config/url";

const SellerProductVisibilityPage = () => {
  const [activeTab, setActiveTab] = useState("Sale By Seller");
  const [sellerData, setSellerData] = useState([]);
  const [ogcsData, setOgcsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const LIMIT = 5;

  // ================= FETCH =================
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const sellerRes = await axios.get(
          `${baseurl}/api/admin/sellerPayment`
        );

        const ogcsRes = await axios.get(
          `${baseurl}/api/admin/sellerDepositPayments`
        );

        setSellerData(sellerRes.data || []);
        setOgcsData(ogcsRes.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ================= FILTER =================
  const currentData =
    activeTab === "Sale By Seller" ? sellerData : ogcsData;

  const filtered = currentData.filter((item) => {
    const val = search.toLowerCase();

    return (
      item?.seller?.sellerName?.toLowerCase().includes(val) ||
      item?.seller?.sellerEmail?.toLowerCase().includes(val) ||
      item?.sellerId?.name?.toLowerCase().includes(val) ||
      item?.sellerId?.email?.toLowerCase().includes(val) ||
      item?.products?.some((p) =>
        (p.productName || p.productId?.name || "")
          .toLowerCase()
          .includes(val)
      )
    );
  });

  // ================= PAGINATION =================
  const totalPages = Math.ceil(filtered.length / LIMIT);
  const start = (page - 1) * LIMIT;
  const paginated = filtered.slice(start, start + LIMIT);

  // ================= STATUS COLOR =================
  const getStatusColor = (status) => {
    if (!status) return "bg-gray-400";

    switch (status.toLowerCase()) {
      case "paid":
        return "bg-green-500";
      case "pending":
        return "bg-yellow-500";
      case "failed":
        return "bg-red-500";
      default:
        return "bg-gray-400";
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Seller Payments</h1>

          <input
            type="text"
            placeholder="Search seller / product..."
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="border px-3 py-2 rounded-lg w-72"
          />
        </div>

        {/* TABS */}
        <div className="flex gap-4 mb-4">
          {["Sale By Seller", "Sale By OGCS"].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setPage(1);
              }}
              className={`px-4 py-2 rounded ${
                activeTab === tab
                  ? "bg-blue-600 text-white"
                  : "bg-white border"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* TABLE */}
        <div className="bg-white shadow rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-6 text-center">Loading...</div>
          ) : paginated.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No data found
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3 text-left">Seller</th>
                  <th className="p-3 text-left">Products</th>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Payment</th>
                  <th className="p-3">Date</th>
                </tr>
              </thead>

              <tbody>
                {paginated.map((item, index) => (
                  <tr key={index} className="border-t hover:bg-gray-50">

                    {/* SELLER */}
                    <td className="p-3">
                      <div className="font-semibold">
                        {activeTab === "Sale By OGCS"
                          ? item.sellerId?.name
                          : item.seller?.sellerName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {activeTab === "Sale By OGCS"
                          ? item.sellerId?.email
                          : item.seller?.sellerEmail}
                      </div>
                    </td>

                    {/* PRODUCTS */}
                    <td className="p-3">
                      {item.products?.map((p, i) => (
                        <div key={i} className="text-xs">
                          •{" "}
                          {activeTab === "Sale By OGCS"
                            ? p.productId?.name
                            : p.productName}{" "}
                          - ₹
                          {activeTab === "Sale By OGCS"
                            ? p.totalProductAmount
                            : p.productPrice}
                        </div>
                      ))}
                    </td>

                    {/* AMOUNT */}
                    <td className="p-3 text-center font-semibold">
                      ₹{item.totalAmount}
                    </td>

                    {/* PAYMENT */}
                    <td className="p-3 text-center">
                      <div
                        className={`text-white px-2 py-1 rounded text-xs inline-block ${getStatusColor(
                          item.paymentDetails?.paymentStatus
                        )}`}
                      >
                        {item.paymentDetails?.paymentStatus || "N/A"}
                      </div>

                      <div className="text-xs mt-1 text-gray-500">
                        Order: {item.paymentDetails?.orderCreationId || "-"}
                      </div>
                    </td>

                    {/* DATE */}
                    <td className="p-3 text-center text-xs">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Prev
            </button>

            <span>
              {page} / {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default SellerProductVisibilityPage;